import React, {useEffect, useState} from 'react';
import './css/App.scss';
import {Project} from "./interfaces/interfaces";
import {Button, Card, Footer, TextInput, ToggleSwitch, Badge} from 'flowbite-react';
import {BsTwitter, BsGithub, BsDiscord, BsX} from "react-icons/bs";
import {ImList} from "react-icons/im";
import {TfiLayoutGrid3Alt} from "react-icons/tfi";
import {IoMdArrowDropup} from "react-icons/io";

import DefaultModal from "./components/shared/modal";
import {useNavigate} from 'react-router-dom';

function App() {
    const [projects, setProjects] = useState<Project[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [stakingFilter, setStakingFilter] = useState(false);
    const [marketFilter, setMarketFilter] = useState(false);
    const itemsPerPage = 12; // Number of items to display per page
    const [displayedItems, setDisplayedItems] = useState(itemsPerPage);
    const [gridLayout, setGridLayout] = useState<'one-per-row' | 'three-per-row'>('one-per-row');
    // Add state for grid layout icon
    const [gridIcon, setGridIcon] = useState<JSX.Element>(<ImList/>);
    const [showScrollTop, setShowScrollTop] = useState(false);

    const ethereumIconPath = '/images/ethereum-eth-logo.svg'; // Replace with the actual path to the Ethereum SVG icon
    const solanaIconPath = '/images/solana-sol-logo.svg'; // Replace with the actual path to the Solana SVG icon
    const polygonIconPath = '/images/polygon-matic-logo.svg'; // Replace with the actual path to the Polygon SVG icon

    const label = 'Collaboration Policy';
    const customTitle = "Collaboration Policy";

    const navigate = useNavigate();

    const customContent = (
        <div className="space-y-6">
            <div className="text-base leading-relaxed">
                At Octopeeps, transparency is our cornerstone for building trust, and we take our reputation very
                seriously. That is why we have a stringent collaboration policy. Please review if your project meets the
                following requirements:

                <ol className="list-decimal space-y-2 container">
                    <li>
                        Your project should be active for at least six months, and any social network activity should be
                        genuine and not manipulated.
                    </li>
                    <li>
                        Your team should not be anonymous, or if your project is well-established with a reputation, or
                        we have prior knowledge of each other.
                    </li>
                    <li>
                        Your team must be non-anonymous, showing real people behind the profiles. See an example here:
                        <a className=" font-medium text-blue-600 dark:text-blue-500 hover:underline cursor-pointer"
                           href="https://whitepaper.octopeeps.com/community/team" rel="noreferrer" target="_blank">
                            OctoPeeps Team
                        </a>
                    </li>
                    <li>
                        Your Twitter account should have a substantial following from reputable members of the community
                        we trust.
                    </li>
                    <li>
                        Your project's roadmap should clearly outline the specifics of what you are building. However, a
                        white paper is not mandatory. You can refer to this example:
                        <a className="font-medium text-blue-600 dark:text-blue-500 hover:underline cursor-pointer"
                           href="https://whitepaper.octopeeps.com/" rel="noreferrer" target="_blank">
                            OctoPeeps White Paper
                        </a>
                    </li>
                    <li>
                        All official links you share on your social networks must be valid and consistent.
                    </li>
                    <li>
                        If your project involves staking utility, your website should have a corresponding page. You can
                        find an example here:
                        <a className="font-medium text-blue-600 dark:text-blue-500 hover:underline cursor-pointer"
                           href="https://octopeeps.com/dapps" rel="noreferrer" target="_blank">
                            OctoPeeps DApps
                        </a>
                    </li>
                    <li>
                        If your project fails to meet at least one of these points, it is likely we will decline
                        collaboration.
                    </li>
                </ol>
            </div>
        </div>
    );

    // Function to update grid layout icon based on the active grid
    useEffect(() => {
        if (gridLayout === 'one-per-row') {
            setGridIcon(<ImList/>);
        } else if (gridLayout === 'three-per-row') {
            setGridIcon(<TfiLayoutGrid3Alt/>);
        }
    }, [gridLayout]);

    useEffect(() => {
        const fetchProjects = async () => {
            try {
                const response = await fetch(process.env.PUBLIC_URL + '/projects.json');
                const data = await response.json();

                // Only consider projects that are not archived or have an undefined archived status (for backward compatibility)
                const activeProjects = data.filter((project: Project) => project.archived === undefined || !project.archived);

                // Shuffle the projects array
                const shuffledProjects: Array<Project> = shuffleArray(activeProjects);

                setProjects(shuffledProjects);

                setLoading(false);
            } catch (error) {
                console.error(error);
                setLoading(false);
            }
        };

        fetchProjects();
    }, []);

    useEffect(() => {
        // Add scroll event listener on mount
        window.addEventListener('scroll', handleScroll);

        // Clean up the event listener on unmount
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    // Function to scroll the page to the top
    const scrollToTop = () => {
        window.scrollTo({top: 0, behavior: 'smooth'});
    };

    const navigateTo = (url: string) => {
        navigate(url);
    };

    // Function to shuffle the array using Fisher-Yates algorithm
    const shuffleArray = (array: Array<any>): Array<any> => {
        const newArray = [...array];
        for (let i = newArray.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
        }
        return newArray;
    };

    // Function to get the appropriate SVG icon based on the blockchain value   
    const getBlockchainIcon = (blockchain: any) => {        
        switch (blockchain) {
            case 'ethereum':
                return ethereumIconPath;
            case 'solana':
                return solanaIconPath;
            case 'polygon':
                return polygonIconPath;
            default:                
                return null; // Return null or a default icon path if the blockchain value is not recognized
                
        }
    };
    

    // Filter projects based on the search query and filter checkboxes
    let filteredProjects = projects.filter((project: Project) => {
        const nameMatch = project.projectName.toLowerCase().includes(searchQuery.toLowerCase());
        const stakingMatch = !stakingFilter || project.stakingLink;
        const marketMatch = !marketFilter || project.marketLink;
        return nameMatch && stakingMatch && marketMatch;
    });

    // Determine which projects to display based on the search query and filter checkboxes
    const projectsToDisplay = searchQuery || stakingFilter || marketFilter ? filteredProjects : projects;

    // Slice the projectsToDisplay array based on the number of displayed items
    const projectsOnCurrentPage = projectsToDisplay.slice(0, displayedItems);

    const handleClearSearch = () => {
        setSearchQuery('');
    };

    // Toggle function to change grid layout
    const toggleGridLayout = () => {
        setGridLayout(prevLayout => (prevLayout === 'one-per-row' ? 'three-per-row' : 'one-per-row'));
    };

    // Function to handle "Load More" button click
    const handleLoadMore = () => {
        // Increase the number of items displayed by the value of itemsPerPage
        setDisplayedItems(prevDisplayedItems => prevDisplayedItems + itemsPerPage);
    };

    const handleScroll = () => {
        if (window.scrollY > 200) {
            setShowScrollTop(true);
        } else {
            setShowScrollTop(false);
        }
    };


    return (
        <div className="App">
            <header className="App-header" style={{
                minHeight: '100vh'
            }}>
                <div className="container flex flex-col items-center">
                    <img className="logo"
                         src="/images/hub_with_octo_transparent.png"
                         alt="Only Fams Logo"
                    />
                    <p className="xs:text-2xl lg:text-4xl mb-3">Find your fam with ease</p>
                    <p className="text-2xl font-bold mb-4">
                        {projects.length} projects featured
                    </p>

                    {/* Search Field */}

                    <div className="search-container mb-4">
                        <TextInput
                            type="text"
                            placeholder="Search projects... 🔍"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />

                        {searchQuery && (
                            <span className="clear-icon" onClick={handleClearSearch}>
                            <BsX color={'black'}/>
                        </span>
                        )}
                    </div>

                    <div className="flex flex-row items-center justify-center content-center gap-4">

                        {/* Filter Checkboxes */}
                        <div className="filter-checkboxes">
                            <ToggleSwitch
                                color={'success'}
                                checked={stakingFilter}
                                label={'Staking'}
                                onChange={() => setStakingFilter(!stakingFilter)}
                            />
                        </div>

                        <div className="filter-checkboxes">
                            <ToggleSwitch
                                color={'success'}
                                checked={marketFilter}
                                label={'Market'}
                                onChange={() => setMarketFilter(!marketFilter)}
                            />
                        </div>

                        {/* View Grid Toggle Buttons */}
                        <div className="grid-toggle-container">
                            <div className="grid-toggle-buttons">
                                <Button
                                    color={'gray'}
                                    onClick={toggleGridLayout} className={gridLayout === 'one-per-row' ? 'active' : ''}>
                                    {gridIcon}
                                </Button>
                            </div>
                        </div>

                        {/* View Grid Toggle Buttons */}
                        <Button gradientDuoTone="purpleToBlue"
                                onClick={() => navigateTo('/ogmeter')}>
                            OG METER
                        </Button>

                    </div>

                </div>

                {/* Card List */}
                <div
                    className={gridLayout === 'one-per-row' ? 'grid xs:grid-cols-1' : 'grid xs:grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'}>
                    {loading ? (
                        <div className="container">Loading...</div>
                    ) : projectsOnCurrentPage.length === 0 ? (
                        <div className="container mb-4"
                             style={{
                                 minHeight: '100vh'
                             }}>No projects found 😕</div>
                    ) : (
                        projectsOnCurrentPage.map((project: Project) => (
                            <div className="container" key={project.id}>
                                <Card
                                    className="card flex-1"
                                    style={{
                                        backgroundImage: `url('${project.bannerUrl}')`,
                                        backgroundPosition: 'center',
                                        backgroundSize: 'cover',
                                        backgroundRepeat: 'no-repeat',
                                        border: 'none',
                                        minWidth: '100%'
                                    }}
                                >

                                    {/* Blockchain Icons */} 
                                    {project.blockchain && (
                                        <div
                                            style={{
                                                position: 'absolute',
                                                top: '0.2rem',
                                                left: '0.2rem',
                                                display: 'flex',
                                                gap: '0.25rem',
                                            }}
                                        >
                                            {(Array.isArray(project.blockchain) ? project.blockchain : [project.blockchain])
                                                .map((chain, index) => {
                                                    const normalizedChain = chain?.toLowerCase().trim();
                                                    const icon = getBlockchainIcon(normalizedChain);  

                                                    if (!icon) return null;
                                                    return (
                                                        <Badge key={index} style={{ borderRadius: '50%' }} color="gray" size="sm">
                                                            <img
                                                                className="blockchain-logo"
                                                                src={icon}
                                                                alt={normalizedChain}
                                                            />
                                                        </Badge>
                                                    );
                                                })}
                                        </div>
                                    )}
                                   
                                     {/* Blockchain Icons old code */} 
                                    {/* {project.blockchain && (
                                        <div
                                            style={{
                                                position: 'absolute', // Position the blockchain icon absolutely inside the card container
                                                top: '0.2rem', // Adjust top position as needed
                                                left: '0.2rem', // Adjust left position as needed
                                            }}
                                        >
                                            <Badge
                                                style={{
                                                    borderRadius: '50%'
                                                }}
                                                color="gray"
                                                size="sm"
                                            >
                                                <img className="blockchain-logo"
                                                     src={getBlockchainIcon(project.blockchain)!} // Call the function to get the appropriate icon path
                                                     alt={project.blockchain}
                                                />
                                            </Badge>
                                        </div>
                                    )} */}

                                    <div
                                        className="font-normal text-gray-700 dark:text-gray-400 flex flex-row flex-wrap items-center justify-center gap-2">
                                        <div className="badge-container">
                                            {project.websiteLink ? (
                                                <a href={project.websiteLink} target="_blank" rel="noreferrer">
                                                    <Button size={'xs'} color={'dark'} className="whitespace-nowrap">
                                                        {project.projectName}
                                                    </Button>
                                                </a>
                                            ) : null}
                                        </div>
                                        {project.stakingLink ? (
                                            <a href={project.stakingLink} target="_blank" rel="noreferrer">
                                                <Button gradientDuoTone="purpleToPink" size={'xs'}>
                                                    Staking
                                                </Button>
                                            </a>
                                        ) : null}

                                        {project.marketLink ? (
                                            <a href={project.marketLink} target="_blank" rel="noreferrer">
                                                <Button gradientDuoTone="purpleToPink" size={'xs'}>
                                                    Market
                                                </Button>
                                            </a>
                                        ) : null}

                                        {project.mintingPage ? (
                                            <a href={project.mintingPage} target="_blank" rel="noreferrer">
                                                <Button gradientDuoTone="purpleToPink" size={'xs'}>
                                                    Mint
                                                </Button>
                                            </a>
                                        ) : null}

                                        {project.whitePaperLink ? (
                                            <a href={project.whitePaperLink} target="_blank" rel="noreferrer">
                                                <Button gradientDuoTone="purpleToPink" size={'xs'}>
                                                    WP
                                                </Button>
                                            </a>
                                        ) : null}

                                        {project.tokenLink ? (
                                            <a href={project.tokenLink} target="_blank" rel="noreferrer">
                                                <Button gradientDuoTone="purpleToPink" size={'xs'}>
                                                    Token
                                                </Button>
                                            </a>
                                        ) : null}

                                        {project.utilityLink ? (
                                            <a href={project.utilityLink} target="_blank" rel="noreferrer">
                                                <Button gradientDuoTone="purpleToPink" size={'xs'}>
                                                    Utility
                                                </Button>
                                            </a>
                                        ) : null}

                                        {project.reportLink ? (
                                            <a href={project.reportLink} target="_blank" rel="noreferrer">
                                                <Button gradientDuoTone="purpleToPink" size={'xs'}>
                                                    Report
                                                </Button>
                                            </a>
                                        ) : null}
                                    </div>
                                </Card>
                            </div>
                        ))
                    )}
                </div>

                {/* Load More Button */}
                {displayedItems < projectsToDisplay.length && (
                    <div className="container flex justify-center">
                        <Button
                            gradientDuoTone="tealToLime"
                            onClick={handleLoadMore}>Load More</Button>
                    </div>
                )}


            </header>

            <div className="flex items-center justify-center flex-col">
                <p className="text-gray-50 p-2">
                    Ping us on Twitter or Discord to be added -
                    <DefaultModal label={label} title={customTitle} content={customContent}/>
                </p>
            </div>

            <div className="w-full bg-gray-900 px-4 py-6 flex justify-center items-center flex-col gap-2">
                <Footer.Copyright
                    by="Only Fams"
                    href="https://fam.octopeeps.com/"
                    year={2023}
                />

                <div className="mt-4 flex space-x-6 sm:mt-0 sm:justify-center">
                    <Footer.Icon
                        href="https://twitter.com/OctoPeepsNFTs"
                        target="_blank"
                        icon={BsTwitter}
                    />
                    <Footer.Icon
                        href="https://discord.gg/MfMTwyP6b9"
                        target="_blank"
                        icon={BsDiscord}
                    />
                    <Footer.Icon
                        href="https://github.com/Solonnikov"
                        target="_blank"
                        icon={BsGithub}
                    />
                </div>
            </div>

            {/* Scroll to Top Button */}
            {showScrollTop && (
                <div className="scroll-top-button-container">
                    <Button
                        gradientDuoTone="redToYellow"
                        onClick={scrollToTop}>
                        <IoMdArrowDropup/>
                    </Button>
                </div>
            )}

        </div>
    );
}

export default App;
